// src/forms/data_hub.js
import {
    return_valid_or_default_forms_domain,
    inject_styles
} from '../helpers/index';

import { addDataHubMessageListener } from '../forms/cors_iframe';
import { bind_all_tel_inputs_intl } from '../tel/telephone_sms_inputs';
import defaultsDeep from 'lodash.defaultsdeep';
import { load_user_data_from_jwt_url_params } from '../helpers/get_process_user_data';
import { get_most_parent_url_params } from '../helpers/parse_url_params';
import { default_forms_domain, default_theme } from '../helpers/static_vars';
import { show_error_popover } from './html_element_helpers/error_popover';
import { log_console_error_and_track_to_action_endpoint_with_rate_limit } from './html_element_helpers/error_tracking';
import { run_theme_json_to_base64 } from './html_element_helpers/form_styling';
import {
    add_form_input_loading_state,
    remove_form_input_loading_state,
    add_form_loading_overlay,
    remove_form_loading_overlay,
    prefill_form
} from '../forms/shared_form_ux_functions';
import { updateActblueConfigWithDatahub, init_actblue } from '../helpers/actblue_integration';
import { v4 as uuidv4 } from 'uuid';

// Standalone functions that take data_hub_instance as first param.
// Aliased so class methods can share the same names without recursion, and JSDoc {@link …} jumps here then into the module.
import {
    load_client_public_metadata as load_client_public_metadata_from_metadata_module,
    set_page_metadata_defaults as set_page_metadata_defaults_from_metadata_module
} from './data_hub_metadata';
import { got_cors_user_data_cache as got_cors_user_data_cache_from_cors_user_data_module } from './data_hub_cors_user_data';
import {
    got_clear_jwt_response as got_clear_jwt_response_from_forget_user_module,
    forget_user as forget_user_from_forget_user_module
} from './data_hub_forget_user';

export class data_hub_class {
    constructor(config = {}, callback_function_after_init = () => { }) {
        const default_error_function = (internal_error_message, external_error_message) => {
            log_console_error_and_track_to_action_endpoint_with_rate_limit({
                internal_error_message,
                external_error_message,
                data_hub_instance: this,
                user_data: this.user_data || {},
                metadata: this.page_metadata || {},
                maximum_error_tracking_requests_per_page: 3
            });
            show_error_popover(external_error_message, this.target || document);
        };
        this.error_function = config.error_function || default_error_function;

        // Set iframe cors listener passing the reference
        addDataHubMessageListener(this);

        // Start ActBlue integration (load its configuration)
        this.has_init_actblue_merged_configuration = false;
        // Errors are handled inside init_actblue via error_function; promise resolves after catch.
        init_actblue(this.error_function);

        this.client_metadata = {};

        this.forms_domain = return_valid_or_default_forms_domain(config.forms_domain || default_forms_domain);
        this.url_params = get_most_parent_url_params(this.error_function);
        this.iterableCampaignId = null;
        this.iterableTemplateId = null;

        // Local set for prefilled forms
        this.prefilled_forms = new Set();

        // ID to compare uniqueness of data_hub inits
        this.data_hub_init_id = uuidv4();

        // Global shared user_data object
        this.user_data = (config.user_data && typeof config.user_data === 'object') ? config.user_data : {};
        this.jwt_cookie = null;

        this.actblue_token = config.actblue_token || null;

        this.actblue_amounts = config.actblue_amounts || [5,10,20,50,100];
        this.actblue_default_page_url = config.actblue_default_page_url || null;
        this.actblue_preview = config.actblue_preview || false;
        this.actblue_css_url = config.actblue_css_url || 'https://qtera.org/css/actblue_modal_600_max.css';

        this.bind_tel_inputs_intl = config.bind_tel_inputs_intl || false;

        // Setup promise to signal when user data is loaded
        this.user_data_loaded = false;
        this.user_data_loaded_promise = new Promise((resolve, reject) => {
            this.resolve_user_data_loaded = () => {
                this.user_data_loaded = true;
                return resolve();
            };
            this.reject_user_data_loaded = (error) => {
                this.user_data_loaded = false;
                return reject(error);
            };
        }).then(async () => {
            this.user_data = defaultsDeep(this.user_data, window.data_hub_user_data);

            updateActblueConfigWithDatahub(this);

            document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
                detail: {
                    user_data: this.user_data || {},
                    prefill_input_selector: this.prefill_input_selector || null,
                    data_hub_instance: this,
                    target: this.target || document
                },
                bubbles: true,
                composed: true
            }));

            if (this.bind_tel_inputs_intl) {
                this.iti_inputs = await bind_all_tel_inputs_intl(
                    this.target || document,
                    this
                );
            }

        })
            .catch((error) => {
                this.error_function?.('Error in user_data_loaded_promise: ' + error, null);
            })

        // Create page metadata and load Iterable IDs
        set_page_metadata_defaults_from_metadata_module(this, config);

        // Prefill settings
        this.target = config.target || null;
        this.prefill_input_selector = '[data-allow-data-hub-prefill="true"]';
        this.reset_button_selector = '.data-hub-reset-form';
        this.prefill_all_forms = config.prefill_all_forms ?? true;
        this.block_page_on_user_data_load = config.block_page_on_user_data_load ?? true;
        this.prefill_timeout = config.prefill_timeout || 10000;
        this.has_hit_prefill_timeout = false;
        this.get_iterable_user_data_via_hmac_key = config.get_iterable_user_data_via_hmac_key ?? true;
        this.callback_function_after_init = callback_function_after_init;

        this.is_data_hub_form_submission_in_progress = false;
        this.is_initialized = false;

        // Inject theme styles if provided
        config.theme = config.theme || {};
        const theme = defaultsDeep({}, config.theme, default_theme);
        if (config.style_target) {
            inject_styles(theme, config.style_target);
        }

        // Begin initialization
        this.start_data_hub_init();
    }

    callDataHubDispatchEventThenCallbackInit() {
        this.is_initialized = true;

        // Ensure the instance is accessible globally so late consumers can find it
        if (typeof window !== 'undefined' && !window.data_hub) {
            window.data_hub = this;
        }

        document.dispatchEvent(new CustomEvent('data-hub-init', {
            detail: {
                data_hub_instance: this
            },
            bubbles: true,
            composed: true
        }));
        this.callback_function_after_init(this);
    }

    async start_data_hub_init() {
        if (this.prefill_all_forms) {
            add_form_input_loading_state(this.target || document, this.prefill_input_selector);
            add_form_loading_overlay(this.target || document);
        }

        // Maximum time to allow GET user_data. Remove any loading state if hit timeout.
        // But i believe the default is the form shows no loading right now. It will prefill overwrite
        // any non active, non edited marked inputs on response
        if (this.prefill_timeout) {
            setTimeout(() => {
                this.has_hit_prefill_timeout = true;
                remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
                remove_form_loading_overlay(this.target || document);
            }, this.prefill_timeout);
        }

        // Load client metadata
        await load_client_public_metadata_from_metadata_module(this).then((metadata) => {
            if (metadata && typeof metadata === 'object') {
                this.client_metadata = metadata;
            } else {
                try {
                    this.client_metadata = JSON.parse(metadata);
                } catch (error) {
                    this.error_function?.('Error parsing client metadata: ' + error, null);
                    this.client_metadata = {};
                }
            }
            if (this.client_metadata?.actblue_default_token) {
                this.actblue_token = this.client_metadata.actblue_default_token;
            }
            if (this.client_metadata?.actblue_default_page_url) {
                this.actblue_default_page_url = this.client_metadata.actblue_default_page_url;
            }

            if (this.client_metadata?.forms_domain) {
                this.forms_domain = return_valid_or_default_forms_domain(this.client_metadata.forms_domain);
            }
        });

        // Wait to try and get the updated forms_domain from client_metadata
        try {
            // Chain the two data sources and merge them.
            await load_user_data_from_jwt_url_params(this);

            // Always remove loading state when data arrives — don't rely solely on the safety-net timeout.
            // fill_element_with_data skips fields the user has already typed into (data-user-modified).
            remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
            remove_form_loading_overlay(this.target || document);
            if (this.prefill_all_forms) {
                const prefill_root = this.target || document;
                prefill_form(
                    prefill_root,
                    this.user_data,
                    this.prefill_input_selector,
                    this.reset_button_selector,
                    null,
                    false,
                    this
                );
            }
            this.resolve_user_data_loaded();
            this.callDataHubDispatchEventThenCallbackInit();
        } catch (error) {
            // Also remove loading on error — don't leave the form stuck in loading state
            remove_form_input_loading_state(this.target || document, this.prefill_input_selector);
            remove_form_loading_overlay(this.target || document);
            this.reject_user_data_loaded(error);
            this.callDataHubDispatchEventThenCallbackInit();
        }
    }

    // Static helper to convert theme JSON to base64
    static theme_json_to_base64(theme) {
        return run_theme_json_to_base64(theme);
    }

    // Reference wrappers to clean up this file

    /** @see {@link set_page_metadata_defaults_from_metadata_module} */
    set_page_metadata_defaults(config) {
        return set_page_metadata_defaults_from_metadata_module(this, config);
    }

    /** @see {@link load_client_public_metadata_from_metadata_module} */
    load_client_public_metadata() {
        return load_client_public_metadata_from_metadata_module(this);
    }

    /** @see {@link got_cors_user_data_cache_from_cors_user_data_module} */
    got_cors_user_data_cache(payload) {
        return got_cors_user_data_cache_from_cors_user_data_module(this, payload);
    }

    /** @see {@link got_clear_jwt_response_from_forget_user_module} */
    got_clear_jwt_response(payload) {
        return got_clear_jwt_response_from_forget_user_module(this, payload);
    }

    /** @see {@link forget_user_from_forget_user_module} */
    forget_user(options) {
        return forget_user_from_forget_user_module(this, options);
    }
}
